import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Input, ChangeDetectorRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GlobalValidators } from '../../../utils/validators';
import { AccountService } from '../../../services/account.service';
import { PortalProductsService } from '../../../services/portal-products.service';
import { OpenDialog } from '../../dialogs/dialog';
import { ConfirmPasswordComponent } from '../../../portal/confirm-password/confirm-password.component';
import { getEncripted } from '../../../utils/pw-encripted';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-mfa-configuration',
  templateUrl: './mfa-configuration.component.html',
  styleUrls: ['./mfa-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaConfigurationComponent implements OnInit, OnDestroy {
  @Input() currentUser;
  @Output() emitEvent = new EventEmitter<any>();
  mfaTypes = ['PIN', 'OTP'];
  resendingVerificationCodes = false;
  mfaTypeController = new FormControl('PIN', Validators.required);
  mfaConfigForm: FormGroup;
  verifyMfaTypeErrors = null;
  verifyMfaType = false;
  otpVerified = false;

  configuringMfaType = false;
  configureErrors = null;

  mfaConfigured = false;
  mfaConfigureProcess = false;

  pinVerified;

  forgotPin = false;
  newPinVerified = false;
  private langChangeSubscription: Subscription;
  @ViewChild('mfaConfigPin1Row') mfaConfigPin1Row: any;
  @ViewChild('mfaConfigPin2Row') mfaConfigPin2Row: any;
  @ViewChild('mfaConfigPin3Row') mfaConfigPin3Row: any;
  @ViewChild('mfaConfigPin4Row') mfaConfigPin4Row: any;
  @ViewChild('mfaConfigPin5Row') mfaConfigPin5Row: any;
  @ViewChild('mfaConfigPin6Row') mfaConfigPin6Row: any;
  @ViewChild('mfaConfigOtp1Row') mfaConfigOtp1Row: any;
  @ViewChild('mfaConfigOtp2Row') mfaConfigOtp2Row: any;
  @ViewChild('mfaConfigOtp3Row') mfaConfigOtp3Row: any;
  @ViewChild('mfaConfigOtp4Row') mfaConfigOtp4Row: any;

  constructor(
    private cdRef: ChangeDetectorRef,
    private accountService: AccountService,
    private portalProductService: PortalProductsService,
    private router: Router,
    public translate: TranslateService
  ) {
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.cdRef.detectChanges(); // Force change detection
    });
  }

  // Helper method to get element by index
  private getElementByIndex(index: number): any {
    const elements = [
      this.mfaConfigPin1Row,
      this.mfaConfigPin2Row,
      this.mfaConfigPin3Row,
      this.mfaConfigPin4Row,
      this.mfaConfigPin5Row,
      this.mfaConfigPin6Row,
      this.mfaConfigOtp1Row,
      this.mfaConfigOtp2Row,
      this.mfaConfigOtp3Row,
      this.mfaConfigOtp4Row,
    ];
    return elements[index];
  }

  async ngOnInit() {
    // if (!this.currentUser.mfaType) {
    this.mfaConfigureProcess = true;
    // }
    this.mfaTypeController.valueChanges.subscribe(async (val) => {
      if (val === 'PIN') {
        this.bindForm('PIN');
      } else if (val === 'OTP') {
        this.bindForm('OTP');
        await this.sendVerificationCodes();
      }
    });
  }

  bindForm(type) {
    this.verifyMfaTypeErrors = '';
    if (type === 'PIN') {
      this.mfaConfigForm = new FormGroup({
        code: new FormGroup({
          pin1: new FormControl('', GlobalValidators.required),
          pin2: new FormControl('', [GlobalValidators.required]),
          pin3: new FormControl('', [GlobalValidators.required]),
          pin4: new FormControl('', [GlobalValidators.required]),
          pin5: new FormControl('', [GlobalValidators.required]),
          pin6: new FormControl('', [GlobalValidators.required]),
        }),
      });
    } else if (type === 'OTP') {
      this.mfaConfigForm = new FormGroup({
        code: new FormGroup({
          pin1: new FormControl('', GlobalValidators.required),
          pin2: new FormControl('', [GlobalValidators.required]),
          pin3: new FormControl('', [GlobalValidators.required]),
          pin4: new FormControl('', [GlobalValidators.required]),
        }),
      });
    }
    this.cdRef.detectChanges();
    const firstElement = this.getElementByIndex(0);
    if (firstElement?.nativeElement) {
      firstElement.nativeElement.focus();
    }
  }

  changeMfaType(currentType) {
    OpenDialog(ConfirmPasswordComponent, { passwordValidator: true })
      .then(async (data) => {
        this.mfaTypes = ['PIN', 'OTP'];
        this.mfaTypes = this.mfaTypes.filter((type) => type !== currentType);
        this.mfaConfigureProcess = true;
        this.cdRef.detectChanges();
      })
      .catch();
  }

  getCode = (pinArray = ['pin1', 'pin2', 'pin3', 'pin4'], value) => {
    // console.log(pinArray);
    let pinCode = '';
    pinArray.map((field) => {
      pinCode = pinCode + (value[field] ? value[field]?.toString() : '');
    });
    return pinCode;
  };

  async configMfa(form) {
    if (form.invalid) {
      return;
    }
    this.verifyMfaType = true;
    this.cdRef.detectChanges();
    let enteredOTP = form.value['code']
      ? this.getCode(
        (this.mfaTypeController.value === 'PIN' && ['pin1', 'pin2', 'pin3', 'pin4', 'pin5', 'pin6']) || ['pin1', 'pin2', 'pin3', 'pin4'],
        form.value['code']
      )
      : '';
    if (this.mfaTypeController.value === 'PIN') {
      try {
        this.verifyMfaType = true;
        this.cdRef.detectChanges();
        await this.accountService.validatePIN(getEncripted.bind(this)(enteredOTP), false);
        await this.configMfaType('PIN');
        this.pinVerified = true;
        this.verifyMfaType = false;
        this.cdRef.detectChanges();
      } catch (e) {
        this.verifyMfaTypeErrors = e.error.message;
        this.verifyMfaType = false;
        this.cdRef.detectChanges();
      }
    } else if (this.mfaTypeController.value === 'OTP') {
      try {
        this.verifyMfaType = true;
        this.cdRef.detectChanges();
        await this.portalProductService.validateOtp(enteredOTP);
        await this.configMfaType('OTP');
        this.otpVerified = true;
        this.verifyMfaType = false;
        this.cdRef.detectChanges();
      } catch (e) {
        this.verifyMfaTypeErrors = e.error.message;
        this.verifyMfaType = false;
        this.cdRef.detectChanges();
      }
    }
  }

  forgotPinView(type) {
    this.forgotPin = true;
    this.bindForm(type);
    // this.enterPinFG.reset();
    // this.errorMassage = '';
    // this.forgetPinEmitter.emit(false);
    this.cdRef.detectChanges();
  }

  newPinEnterScreenDone(event) {
    // this.forgotPin = false;
    this.cdRef.detectChanges();
  }

  forgetPinFromSetPin() {
    this.forgotPin = false;
    this.cdRef.detectChanges();
  }

  async newPinAdded() {
    this.newPinVerified = true;
    await this.configMfaType('PIN');
    this.cdRef.detectChanges();
  }

  async configMfaType(type) {
    try {
      this.configuringMfaType = true;
      this.cdRef.detectChanges();
      // await this.accountService.setMfaType(type);
      this.mfaConfigured = true;
      this.mfaConfigureProcess = false;
      this.configuringMfaType = false;
      setTimeout(async () => {
        this.emitEvent.emit('true');
      }, 3000);
      this.emitEvent.emit('true');
      // setTimeout(async () => {
      // await this.accountService.signOut();
      // await this.router.navigate(['landing/signin'], { queryParams: { uid: `${this.currentUser.id}` } });
      // }, 3000)
      this.cdRef.detectChanges();
    } catch (e) {
      this.configureErrors = e.errors.message;
      this.configuringMfaType = false;
      this.cdRef.detectChanges();
    }
  }

  async keyUpEvent(event, index) {
    // if (this.mfaErrors) {
    //   this.mfaErrors = '';
    //   this.cdRef.detectChanges();
    // }
    let pos = index;
    if (event.keyCode === 8 || event.keyCode === 37) {
      pos = index - 1;
    } else {
      pos = index + 1;
    }
    if (pos > -1 && pos < (this.mfaTypeController.value === 'PIN' ? 6 : 4)) {
      const targetElement = this.getElementByIndex(pos);
      if (targetElement?.nativeElement) {
        targetElement.nativeElement.focus();
      }
    }

    if (this.mfaConfigForm.valid && index === (this.mfaTypeController.value === 'PIN' ? 5 : 3)) {
      await this.configMfa(this.mfaConfigForm);
    }
    // console.log(index, this.enterOTPFG.valid);
    // if (this.enterOTPFG.valid && index === 3) {
    //   console.log(index, this.enterOTPFG.valid);
    //   await this.validateOTP(this.enterOTPFG);
    // }
  }

  async sendVerificationCodes() {
    try {
      this.resendingVerificationCodes = true;
      this.cdRef.detectChanges();
      await this.portalProductService.sendOTP();
      this.resendingVerificationCodes = false;
      this.cdRef.detectChanges();
    } catch (e) {
      this.resendingVerificationCodes = false;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
